//----------------------------------------------------
// src/context/UserContext.jsx — Professionele User + RBAC context
//----------------------------------------------------
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { API_BASE_URL } from "../../config/api";

const UserContext = createContext();

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);       // user info + roles + permissions
  const [token, setToken] = useState(null);     // access token
  const [loading, setLoading] = useState(true); // voor protect & refresh

  const API_URL = API_BASE_URL;

  /* =====================================================
     🔹 Logout — tokens legen + user flushen
  ===================================================== */
  const logout = useCallback(() => {
    localStorage.removeItem("accessToken");
    setToken(null);
    setUser(null);
  }, []);

  /* =====================================================
     🔹 Login — token opslaan + user decoderen
  ===================================================== */
  const login = useCallback((jwtToken) => {
    try {
      localStorage.setItem("accessToken", jwtToken);
      const decoded = jwtDecode(jwtToken);

      setToken(jwtToken);
      setUser({
        userId: decoded.userId,
        naam: decoded.naam,
        email: decoded.email,
        tenantId: decoded.tenantId,

        // Oud systeem
        rol: decoded.rol,

        // Nieuw RBAC
        roles: decoded.roles || [],
        permissions: decoded.permissions || [],
      });

    } catch (err) {
      console.error("❌ Fout in login():", err);
      logout();
    }
  }, [logout]);

  /* =====================================================
     🔹 Refresh Token ophalen (automatisch)
  ===================================================== */
  const refreshAccessToken = useCallback(async () => {
    try {
      const res = await axios.post(
        `${API_URL}/api/auth/refresh`,
        {},
        { withCredentials: true }
      );

      if (res.data?.token) {
        login(res.data.token);
      }
    } catch (err) {
      console.warn("⚠️ Refresh token mislukt — user moet opnieuw inloggen.");
      logout();
    }
  }, [API_URL, login, logout]);

  /* =====================================================
     🔹 Bij opstarten → access token ophalen uit localStorage
     🔹 Automatisch refresh-koppeling
  ===================================================== */
  useEffect(() => {
    const saved = localStorage.getItem("accessToken");

    if (saved) {
      try {
        const decoded = jwtDecode(saved);
        const exp = decoded.exp * 1000;
        const now = Date.now();

        if (exp > now) {
          // Token nog geldig → direct gebruiker zetten
          login(saved);
        } else {
          // Token verlopen → refresh token proberen
          refreshAccessToken();
        }
      } catch (err) {
        console.error("❌ Fout bij initial decode:", err);
        logout();
      }
    }

    // Kleine delay om flicker te voorkomen
    setTimeout(() => setLoading(false), 300);
  }, [login, logout, refreshAccessToken]);

  /* =====================================================
     🔹 Helper om permissies te checken
  ===================================================== */
  const hasPermission = (perm) => {
    return user?.permissions?.includes(perm);
  };

  /* =====================================================
     🔹 Export naar children
  ===================================================== */
  return (
    <UserContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        logout,
        hasPermission,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export const useUser = () => useContext(UserContext);
